import React, { useState } from "react";
import * as Mui from "@mui/material";
import { CheckCircleOutline, DeleteOutline, HighlightOff, MoreHoriz } from "@mui/icons-material";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { designColors, designShell } from "services/constants/designTokens";
import { tooltipStyleProps } from "services/constants/Styles";

const MAX_VISIBLE = 2;

// Built at render time so it never reads the tokens while the module loads.
const dangerOutlineSx = () => ({
  ...designShell?.panelActionButton,
  flex: 1,
  minWidth: 0,
  minHeight: 42,
  color: "#E11D48",
  borderColor: "#FDA4AF",
  backgroundColor: designColors?.white,
  "&:hover": { color: "#BE123C", borderColor: "#FB7185", backgroundColor: "#FFF1F3" },
});

// A disabled button can't show a tooltip by itself, so it is wrapped.
const withTooltip = (tooltip, node, key) =>
  tooltip ? (
    <Mui.Tooltip key={key} title={tooltip} arrow slotProps={tooltipStyleProps}>
      <Mui.Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>{node}</Mui.Box>
    </Mui.Tooltip>
  ) : (
    node
  );

const ActionButton = ({ action }) => {
  const Icon = action.icon;
  const icon = Icon ? <Icon sx={{ fontSize: 17 }} /> : null;
  const button =
    action.tone === "primary" ? (
      <PrimaryButton
        key={action.key}
        onClick={action.onClick}
        disabled={action.disabled}
        startIcon={icon}
        sx={{ flex: 1, minWidth: 0, minHeight: 42 }}
      >
        {action.label}
      </PrimaryButton>
    ) : (
      <Mui.Button key={action.key} variant="outlined" onClick={action.onClick} disabled={action.disabled} startIcon={icon} sx={dangerOutlineSx()}>
        {action.label}
      </Mui.Button>
    );
  return withTooltip(action.tooltip, button, action.key);
};

// View panel footer: every action in one row, at most two buttons. Anything
// beyond two goes into the "More actions" (⋯) menu on the left.
// Priority: Reject / Approve (when the entry can still change status), then
// the record action (Delete / Cancel).
//   approval: { status, onApprove, onReject, disabled } or null. An Approved
//     entry gets Reject, a Rejected one gets Approve, a Pending one both.
//   recordAction: { label, onClick, disabled, tooltip } or null.
export const ViewActionFooter = ({ approval, recordAction }) => {
  const [anchor, setAnchor] = useState(null);
  const actions = [];
  if (approval?.onReject && approval.status !== "Rejected") {
    actions.push({ key: "reject", label: "Reject", icon: HighlightOff, tone: "danger", onClick: approval.onReject, disabled: approval.disabled });
  }
  if (approval?.onApprove && approval.status !== "Approved") {
    actions.push({ key: "approve", label: "Approve", icon: CheckCircleOutline, tone: "primary", onClick: approval.onApprove, disabled: approval.disabled });
  }
  if (recordAction) actions.push({ key: "record", icon: DeleteOutline, tone: "danger", ...recordAction });
  if (!actions.length) return null;

  const overflow = actions.length > MAX_VISIBLE;
  const visible = overflow ? actions.slice(0, MAX_VISIBLE) : actions;
  const hidden = overflow ? actions.slice(MAX_VISIBLE) : [];

  return (
    <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
      {hidden.length ? (
        <>
          <Mui.Tooltip title="More actions" arrow slotProps={tooltipStyleProps}>
            <Mui.IconButton
              aria-label="More actions"
              aria-haspopup="menu"
              aria-expanded={Boolean(anchor)}
              onClick={(event) => setAnchor(event.currentTarget)}
              sx={{
                width: 42,
                height: 42,
                flexShrink: 0,
                borderRadius: "10px",
                border: `1px solid ${designColors.slate300}`,
                color: designColors.slate700,
                backgroundColor: designColors.white,
                "&:hover": { backgroundColor: designColors.slateBg, borderColor: designColors.slate400 },
              }}
            >
              <MoreHoriz fontSize="small" />
            </Mui.IconButton>
          </Mui.Tooltip>
          <Mui.Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "bottom", horizontal: "left" }}
            slotProps={{ paper: { sx: { mb: 0.5, minWidth: 200, borderRadius: 2, border: `1px solid ${designColors.slate200}`, boxShadow: "0 8px 24px rgba(16, 24, 40, 0.12)" } } }}
          >
            {hidden.map((action) => {
              const Icon = action.icon;
              const danger = action.tone === "danger";
              const item = (
                <Mui.MenuItem
                  key={action.key}
                  disabled={action.disabled}
                  onClick={() => {
                    setAnchor(null);
                    action.onClick();
                  }}
                  sx={{ fontSize: 14, gap: 1.25, py: 1, color: danger ? "#E11D48" : designColors.slate800 }}
                >
                  {Icon ? <Icon sx={{ fontSize: 18 }} /> : null}
                  {action.label}
                </Mui.MenuItem>
              );
              // A disabled item keeps its reason (e.g. can't cancel a part-repaid loan).
              return action.disabled && action.tooltip ? (
                <Mui.Tooltip key={action.key} title={action.tooltip} placement="right" arrow slotProps={tooltipStyleProps}>
                  <span>{item}</span>
                </Mui.Tooltip>
              ) : (
                item
              );
            })}
          </Mui.Menu>
        </>
      ) : null}
      {visible.map((action) => (
        <ActionButton key={action.key} action={action} />
      ))}
    </Mui.Stack>
  );
};
